import type { SessionLength } from '@/domain/types';
import { SESSION_DURATION_MIN } from '@/domain/pricing';

const MIN_HOLD_MIN = 15;

/** Extra grace a this-week (overbookable) hold earns beyond the standard window
 * (tv-nid): the client has more warning of the near date, so the hold stretches
 * two hours past the flat 24h before it lapses. Out-of-week holds stay firm. */
const THIS_WEEK_GRACE_MS = 2 * 60 * 60 * 1000;

/** Start of `ms`'s LOCAL calendar day. TZ is pinned to Australia/Melbourne in CI
 * and dev, matching queueEngine.gapWindow, so "this week" agrees across both. */
function startOfLocalDay(ms: number): Date {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * True when `slotStartIso` falls inside the current Mon-Sun week: from the start
 * of today's local day up to (not including) the start of next Monday. Replicates
 * `queueEngine.gapWindow(now, 'current')` (today -> end of this week) rather than
 * importing it, so depositWindow keeps no queue-engine dependency (tv-nid).
 */
export function isThisWeek(slotStartIso: string, nowIso: string): boolean {
  const today = startOfLocalDay(new Date(nowIso).getTime());
  const dow = today.getDay(); // 0 Sun .. 6 Sat
  const daysLeft = dow === 0 ? 1 : 8 - dow;
  const windowEnd = new Date(today);
  windowEnd.setDate(today.getDate() + daysLeft);
  const slotMs = new Date(slotStartIso).getTime();
  return slotMs >= today.getTime() && slotMs < windowEnd.getTime();
}

/**
 * 24h hold window. Agreed rule: 24h for slots a week or more out, and 24h inside
 * the week too. (The prior 48h-far-out behaviour was the inverse of the rule.)
 * Signature kept as `24 | 48` for callers; the `slotStartIso` param is retained
 * for the same reason. Spec D2/A7.
 */
export function holdExpiryHours(_slotStartIso: string, _nowIso: string): 24 | 48 {
  return 24;
}

/**
 * The absolute expiry instant = now + the resolved 24/48h window, plus a +2h
 * grace when the slot is this week (tv-nid). A `firm` caller (a Sweeper gap-fill
 * dated-link) opts out of the grace and stays a flat 24h lock, no overbook.
 */
export function holdExpiresAtIso(
  slotStartIso: string,
  nowIso: string,
  opts?: { firm?: boolean },
): string {
  const hours = holdExpiryHours(slotStartIso, nowIso);
  const graceMs = !opts?.firm && isThisWeek(slotStartIso, nowIso) ? THIS_WEEK_GRACE_MS : 0;
  return new Date(new Date(nowIso).getTime() + hours * 60 * 60 * 1000 + graceMs).toISOString();
}

/** Default held-slot length for a deposit tier (minutes). */
export function tierDurationMin(session: SessionLength): number {
  return SESSION_DURATION_MIN[session];
}

/** Clamp a desired end to [start + 15min, blockEnd]. */
export function clampEndIso(startIso: string, desiredEndIso: string, blockEndIso: string): string {
  const startMs = new Date(startIso).getTime();
  const min = startMs + MIN_HOLD_MIN * 60 * 1000;
  const max = new Date(blockEndIso).getTime();
  const clamped = Math.min(Math.max(new Date(desiredEndIso).getTime(), min), Math.max(max, min));
  return new Date(clamped).toISOString();
}

/** True when the tier's default length runs past the free block end. */
export function overshoots(startIso: string, tierMin: number, blockEndIso: string): boolean {
  const needEndMs = new Date(startIso).getTime() + tierMin * 60 * 1000;
  return needEndMs > new Date(blockEndIso).getTime();
}

/** Build an ISO timestamp for a decimal hour on a given day. */
export function hourToIso(dayIso: string, hour: number): string {
  const d = new Date(dayIso);
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}
